import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Dialog, DialogBody, DialogFooter, InputGroup, NonIdealState, Spinner } from '@blueprintjs/core';
import {
  DRIVE_FOLDER_MIME_TYPE, GOOGLE_SHEET_MIME_TYPE, GoogleDriveError, extractDriveFileId, formatDriveDate,
  getDriveSpreadsheetMeta, searchDriveSpreadsheets, type DriveFile, type DriveSpreadsheetMeta,
} from '../../features/docs/drive';
import { useGoogleDriveToken } from '../../features/docs/googleToken';
import { ErrorNote, Pill } from './ui';

/**
 * Pick a spreadsheet from Google Drive with the operator's own Google token
 * (the same connection the merchant Docs tab uses). Search by name, browse
 * folders, or paste a Sheets link. The caller downloads a snapshot; nothing
 * stays connected to the sheet afterwards.
 */
export default function DriveSheetPicker({ isOpen, onClose, onPick }: {
  isOpen: boolean;
  onClose: () => void;
  onPick: (meta: DriveSpreadsheetMeta, token: string) => void;
}) {
  const { token, loading, connect } = useGoogleDriveToken();
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [trail, setTrail] = useState<{ id: string; name: string }[]>([]);
  const [linkError, setLinkError] = useState<string | null>(null);
  const [resolving, setResolving] = useState(false);
  const folderId = trail.length ? trail[trail.length - 1].id : undefined;

  useEffect(() => { if (!isOpen) { setText(''); setQuery(''); setTrail([]); setLinkError(null); } }, [isOpen]);

  const list = useQuery({
    queryKey: ['lead-import', 'drive-sheets', token ? 'connected' : 'none', query, folderId ?? 'recent'],
    queryFn: () => searchDriveSpreadsheets(token as string, { query, folderId: query ? undefined : folderId }),
    enabled: isOpen && !!token,
    staleTime: 30_000,
  });

  const choose = async (fileId: string) => {
    if (!token) return;
    setResolving(true);
    setLinkError(null);
    try {
      onPick(await getDriveSpreadsheetMeta(fileId, token), token);
    } catch (e) {
      setLinkError((e as Error).message);
    } finally {
      setResolving(false);
    }
  };

  const submit = () => {
    const id = extractDriveFileId(text);
    if (id && /google\.com|^[a-zA-Z0-9_-]{25,}$/.test(text.trim())) { choose(id); return; }
    setTrail([]);
    setQuery(text.trim());
  };

  const authError = list.error instanceof GoogleDriveError && (list.error.code === 'auth' || list.error.code === 'access');

  return (
    <Dialog isOpen={isOpen} onClose={onClose} title="Import from Google Sheets" style={{ width: 720 }}>
      <DialogBody>
        {loading ? <Spinner size={20} /> : !token || authError ? (
          <NonIdealState icon="cloud" title="Connect Google Drive"
            description="Use the Google account that received the vendor sheet. The CRM reads it with your access only."
            action={<Button intent="primary" onClick={() => connect()}>Connect Google Drive</Button>} />
        ) : (
          <div className="li-stack">
            <form className="li-row" onSubmit={(e) => { e.preventDefault(); submit(); }}>
              <InputGroup fill leftIcon="search" value={text} placeholder="Spreadsheet name, or paste a Google Sheets link"
                onChange={(e) => setText(e.target.value)} style={{ minWidth: 460 }} autoFocus />
              <Button type="submit" intent="primary" loading={resolving}>Find</Button>
            </form>
            <div className="li-row li-wrap li-muted">
              <Button small minimal onClick={() => { setTrail([]); setQuery(''); setText(''); }}>Recent spreadsheets</Button>
              <Button small minimal onClick={() => { setQuery(''); setTrail([{ id: 'root', name: 'My Drive' }]); }}>Browse My Drive</Button>
              {trail.map((t, i) => (
                <Button key={t.id} small minimal onClick={() => setTrail(trail.slice(0, i + 1))}>/ {t.name}</Button>
              ))}
              {query && <span>Results for “{query}”</span>}
            </div>
            <ErrorNote error={linkError} />
            {!authError && <ErrorNote error={list.error} />}
            {list.isLoading ? <Spinner size={20} /> : (list.data ?? []).length === 0 ? (
              <div className="li-empty">No spreadsheets found.</div>
            ) : (
              <div className="li-tablewrap" style={{ maxHeight: 380 }}>
                <table className="li-table li-table--compact">
                  <thead><tr><th>Name</th><th>Type</th><th>Modified</th><th /></tr></thead>
                  <tbody>
                    {(list.data ?? []).map((f: DriveFile) => {
                      const folder = f.mimeType === DRIVE_FOLDER_MIME_TYPE;
                      return (
                        <tr key={f.id} className="is-clickable"
                          onClick={() => (folder ? (setQuery(''), setTrail([...trail, { id: f.id, name: f.name }])) : choose(f.id))}>
                          <td><span className="li-row">{f.iconLink && <img src={f.iconLink} alt="" width={14} height={14} />}<b>{f.name}</b></span></td>
                          <td>{folder ? 'Folder' : f.mimeType === GOOGLE_SHEET_MIME_TYPE ? <Pill tone="success">Google Sheet</Pill> : f.mimeType.includes('csv') ? 'CSV' : 'Excel'}</td>
                          <td>{formatDriveDate(f.modifiedTime)}</td>
                          <td>{folder ? 'Open' : <span className="li-btn li-btn--link">Import</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <p className="li-note">Imports a copy of the sheet as it is now, all tabs included. Later edits to the sheet are not picked up.</p>
          </div>
        )}
      </DialogBody>
      <DialogFooter actions={<Button onClick={onClose}>Close</Button>} />
    </Dialog>
  );
}
